import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useApp } from '../lib/app-context';
import { Icon } from './Icon';

/*
 * Date and time fields that look and read the same in every browser. The native inputs follow the
 * browser's own locale (mm/dd/yyyy, AM/PM), so these keep day-first dates and 24-hour times, accept
 * typing (digits only, separators are added for you) and offer a calendar with quick picks.
 * Values stay in the wire formats the API expects: YYYY-MM-DD and HH:MM.
 */

const pad = (n: number) => String(n).padStart(2, '0');
const toIso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
/** Parses YYYY-MM-DD as a local date (never UTC, which would shift the day). */
function fromIso(v: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getMonth() === Number(m[2]) - 1 ? d : null;
}
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const sameDay = (a: Date | null, b: Date | null) => !!a && !!b && toIso(a) === toIso(b);

function sepFor(locale: string) {
  return locale.startsWith('tr') ? '.' : '/';
}
/** "26102026" → "26.10.2026", built up as the user types. */
function formatDigits(digits: string, sep: string) {
  const d = digits.slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}${sep}${d.slice(2)}`;
  return `${d.slice(0, 2)}${sep}${d.slice(2, 4)}${sep}${d.slice(4)}`;
}
function display(v: string, sep: string) {
  const d = fromIso(v);
  return d ? `${pad(d.getDate())}${sep}${pad(d.getMonth() + 1)}${sep}${d.getFullYear()}` : '';
}
function parseDigits(digits: string): string | null {
  if (digits.length !== 8) return null;
  const iso = `${digits.slice(4, 8)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`;
  return fromIso(iso) ? iso : null;
}

export function DateInput({
  id,
  value,
  onChange,
  required,
  invalid,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  invalid?: boolean;
}) {
  const { i18n } = useApp();
  const { t, locale } = i18n;
  const sep = sepFor(locale);
  const [draft, setDraft] = useState(() => display(value, sep));
  const [synced, setSynced] = useState(value);
  // Follow value changes made by the form (reset after submit, a quick pick) without an effect.
  if (synced !== value) {
    setSynced(value);
    setDraft(display(value, sep));
  }
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const weekday = fromIso(value);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const commit = (iso: string) => {
    setSynced(iso);
    setDraft(display(iso, sep));
    onChange(iso);
  };
  const onType = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    setDraft(formatDigits(digits, sep));
    const iso = parseDigits(digits);
    const next = iso ?? '';
    setSynced(next);
    onChange(next);
  };

  return (
    <div className="date-field" ref={wrap}>
      <div className="date-control">
        <input
          ref={input}
          id={id}
          className="input"
          inputMode="numeric"
          autoComplete="off"
          placeholder={t('date.placeholder')}
          value={draft}
          required={required}
          aria-invalid={invalid || (draft.length > 0 && !value) || undefined}
          onChange={(e) => onType(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' && e.altKey) setOpen(true);
          }}
        />
        <button
          type="button"
          className="date-toggle"
          aria-label={t('date.open')}
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name="calendar" size={18} />
        </button>
        {weekday && (
          <span className="date-weekday" aria-hidden>
            {new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(weekday)}
          </span>
        )}
      </div>
      {open && (
        <CalendarPopover
          value={value}
          onPick={(iso) => {
            commit(iso);
            setOpen(false);
            input.current?.focus();
          }}
          onClear={
            required
              ? undefined
              : () => {
                  commit('');
                  setOpen(false);
                  input.current?.focus();
                }
          }
          onClose={() => {
            setOpen(false);
            input.current?.focus();
          }}
        />
      )}
    </div>
  );
}

function CalendarPopover({
  value,
  onPick,
  onClear,
  onClose,
}: {
  value: string;
  onPick: (iso: string) => void;
  onClear?: () => void;
  onClose: () => void;
}) {
  const { i18n } = useApp();
  const { t, locale } = i18n;
  const titleId = useId();
  const selected = fromIso(value);
  const [today] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
  });
  const [focus, setFocus] = useState<Date>(selected ?? today);
  const grid = useRef<HTMLDivElement>(null);

  // Keep keyboard focus on the focused day as it moves (and when the popover opens).
  useEffect(() => {
    grid.current?.querySelector<HTMLButtonElement>(`[data-day="${toIso(focus)}"]`)?.focus();
  }, [focus]);

  const first = new Date(focus.getFullYear(), focus.getMonth(), 1);
  const lead = (first.getDay() + 6) % 7; // weeks start on Monday
  const start = addDays(first, -lead);
  const days = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  const weekdays = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(new Date(2024, 0, 1 + i)),
  );
  const month = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(first);
  const full = new Intl.DateTimeFormat(locale, { dateStyle: 'full' });
  const shiftMonth = (n: number) => {
    const target = new Date(focus.getFullYear(), focus.getMonth() + n, 1);
    const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    setFocus(new Date(target.getFullYear(), target.getMonth(), Math.min(focus.getDate(), last)));
  };
  const onKey = (e: KeyboardEvent) => {
    const moves: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (e.key in moves) {
      e.preventDefault();
      setFocus(addDays(focus, moves[e.key]!));
    } else if (e.key === 'PageUp' || e.key === 'PageDown') {
      e.preventDefault();
      shiftMonth(e.key === 'PageUp' ? -1 : 1);
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      const dow = (focus.getDay() + 6) % 7;
      setFocus(addDays(focus, e.key === 'Home' ? -dow : 6 - dow));
    }
  };
  // Monday of next week.
  const nextWeek = addDays(today, 7 - ((today.getDay() + 6) % 7));
  const quick: [string, Date][] = [
    [t('planner.today'), today],
    [t('date.tomorrow'), addDays(today, 1)],
    [t('date.nextWeek'), nextWeek],
  ];

  return (
    <div
      className="date-popover"
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="date-quick">
        {quick.map(([label, d]) => (
          <button key={label} type="button" className={`chip${sameDay(d, selected) ? ' is-on' : ''}`} onClick={() => onPick(toIso(d))}>
            {label}
          </button>
        ))}
      </div>
      <div className="date-head">
        <button type="button" className="icon-btn date-prev" aria-label={t('date.prevMonth')} onClick={() => shiftMonth(-1)}>
          <Icon name="chevron" size={18} />
        </button>
        <strong id={titleId} aria-live="polite">
          {month}
        </strong>
        <button type="button" className="icon-btn" aria-label={t('date.nextMonth')} onClick={() => shiftMonth(1)}>
          <Icon name="chevron" size={18} />
        </button>
      </div>
      <div className="date-grid" role="grid" ref={grid} onKeyDown={onKey}>
        <div className="date-row" role="row">
          {weekdays.map((w) => (
            <span key={w} className="date-dow" role="columnheader">
              {w}
            </span>
          ))}
        </div>
        {Array.from({ length: 6 }, (_, r) => (
          <div className="date-row" role="row" key={r}>
            {days.slice(r * 7, r * 7 + 7).map((d) => {
              const iso = toIso(d);
              const outside = d.getMonth() !== first.getMonth();
              const cls = ['date-day', outside && 'is-outside', sameDay(d, today) && 'is-today', sameDay(d, selected) && 'is-selected']
                .filter(Boolean)
                .join(' ');
              return (
                <span role="gridcell" key={iso} aria-selected={sameDay(d, selected)}>
                  <button
                    type="button"
                    className={cls}
                    data-day={iso}
                    tabIndex={sameDay(d, focus) ? 0 : -1}
                    aria-label={full.format(d)}
                    aria-current={sameDay(d, today) ? 'date' : undefined}
                    onClick={() => onPick(iso)}
                  >
                    {d.getDate()}
                  </button>
                </span>
              );
            })}
          </div>
        ))}
      </div>
      {onClear && (
        <div className="date-foot">
          <button type="button" className="btn btn-sm" onClick={onClear}>
            {t('date.clear')}
          </button>
        </div>
      )}
    </div>
  );
}

/** "930" → "09:30", "1745" → "17:45"; null when it is not a real time. */
function normaliseTime(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  const four = digits.length <= 2 ? `${digits.padStart(2, '0')}00` : digits.padStart(4, '0').slice(0, 4);
  const h = Number(four.slice(0, 2));
  const m = Number(four.slice(2));
  return h < 24 && m < 60 ? `${pad(h)}:${pad(m)}` : null;
}

const SLOTS = Array.from({ length: 48 }, (_, i) => `${pad(Math.floor(i / 2))}:${i % 2 ? '30' : '00'}`);

/** 24-hour time with typing shortcuts ("9" → 09:00, "1430" → 14:30) and half-hour suggestions. */
export function TimeInput({
  id,
  value,
  onChange,
  required,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  const { i18n } = useApp();
  const listId = `${id}-slots`;
  const [draft, setDraft] = useState(value);
  const [synced, setSynced] = useState(value);
  if (synced !== value) {
    setSynced(value);
    setDraft(value);
  }
  const settle = () => {
    const v = normaliseTime(draft);
    const next = v ?? '';
    setDraft(v ?? draft);
    setSynced(next);
    onChange(next);
  };
  return (
    <>
      <input
        id={id}
        className="input time-input"
        inputMode="numeric"
        autoComplete="off"
        placeholder={i18n.t('time.placeholder')}
        list={listId}
        maxLength={5}
        required={required}
        value={draft}
        aria-invalid={(draft.length > 0 && !normaliseTime(draft)) || undefined}
        onChange={(e) => {
          const v = e.target.value;
          setDraft(v);
          if (/^\d{2}:\d{2}$/.test(v) && normaliseTime(v)) {
            setSynced(v);
            onChange(v);
          }
        }}
        onBlur={settle}
      />
      <datalist id={listId}>
        {SLOTS.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </>
  );
}
